"use client";

import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import Combobox, { type ComboboxOption } from "@/components/frontend/ui/form/inputs/combobox";
import Input from "@/components/frontend/ui/form/inputs/input";
import { Accordion, AccordionItem } from "@/components/frontend/ui/accordion";
import type { CardVariant } from "@/components/frontend/ui/card";
import { cn } from "@/lib/utils";
import { buildCategoryOptions, filterFaqSections, type FaqOverviewRenderedSection } from "./data";

interface FaqOverviewFiltersProps {
	sections: FaqOverviewRenderedSection[];
	showSearchBar: boolean;
	searchPlaceholder: string;
	formVariantId: string;
	sectionColumns: string;
	sectionTitleClass?: string;
	faqCardVariant?: CardVariant;
	allCategoriesLabel: string;
	noResultsTitle: string;
	noResultsDescription: string;
}

export function FaqOverviewFilters({
	sections,
	showSearchBar,
	searchPlaceholder,
	formVariantId,
	sectionColumns,
	sectionTitleClass,
	faqCardVariant,
	allCategoriesLabel,
	noResultsTitle,
	noResultsDescription,
}: FaqOverviewFiltersProps) {
	const [query, setQuery] = useState("");
	const [category, setCategory] = useState("");

	const categoryOptions = useMemo(() => buildCategoryOptions(sections), [sections]);
	const showCategoryFilter = categoryOptions.length > 0;

	const categoryComboboxOptions = useMemo<ComboboxOption[]>(
		() => [{ value: "", label: allCategoriesLabel }, ...categoryOptions],
		[allCategoriesLabel, categoryOptions],
	);
	const categoryValue = useMemo<ComboboxOption | undefined>(
		() => categoryComboboxOptions.find((option) => option.value === category) ?? categoryComboboxOptions[0],
		[category, categoryComboboxOptions],
	);

	const filteredSections = useMemo(() => filterFaqSections(sections, query, category), [sections, query, category]);

	return (
		<>
			{(showSearchBar || showCategoryFilter) && (
				<div
					className={cn(
						`ms-form-${formVariantId}`,
						"mb-8 grid gap-3",
						showSearchBar && showCategoryFilter ? "md:grid-cols-[minmax(0,1fr)_16rem]" : "grid-cols-1",
					)}
				>
					{showSearchBar && (
						<Input type="text" name="q" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={searchPlaceholder} />
					)}
					{showCategoryFilter && (
						<Combobox
							name="category"
							value={categoryValue}
							clearable={false}
							formVariant={formVariantId}
							options={categoryComboboxOptions}
							onValueChange={(value) => setCategory(value?.value ?? "")}
						/>
					)}
				</div>
			)}

			{filteredSections.length > 0 ? (
				<div className={cn("grid gap-x-10 gap-y-12", sectionColumns === "2" ? "md:grid-cols-2" : "grid-cols-1")}>
					{filteredSections.map((section, index) => (
						<section key={section.key ?? index} className="flex flex-col gap-6 ms-faq-section">
							{(section.title.length > 0 || section.description.length > 0) && (
								<div className="flex flex-col gap-3">
									{section.title.length > 0 && <h2 className={sectionTitleClass}>{section.title}</h2>}
									{section.description.length > 0 && <p>{section.description}</p>}
								</div>
							)}

							<Accordion className="flex flex-col gap-4">
								{section.questions.map((item) => (
									<AccordionItem key={item.id} title={item.question} Icon={ChevronDown} cardVariant={faqCardVariant}>
										{item.answer}
									</AccordionItem>
								))}
							</Accordion>
						</section>
					))}
				</div>
			) : (
				<div className="py-12 text-center">
					<h3>{noResultsTitle}</h3>
					<p className="mt-2 text-sm text-muted-foreground">{noResultsDescription}</p>
				</div>
			)}
		</>
	);
}
